import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Button,
  Typography,
  Chip,
  Box,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import LocationCityIcon from "@material-ui/icons/LocationCity";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import StarIcon from "@material-ui/icons/Star";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import PlacesAutocomplete from "react-places-autocomplete";
import {
  HOTEL_CATEGORIES,
  COUNTRIES,
  WHAT_GUESTS_CAN_USE,
  PROPERTY_RATINGS,
  LANGUAGES,
} from "src/utils/misc";
import { asyncAddHotelProperty } from "src/actions/HomeActions";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  section: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5B4FC" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.accent,
    },
  },
  chipSelected: {
    backgroundColor: `${TOKENS.accent} !important`,
    color: "#FFFFFF !important",
    borderColor: `${TOKENS.accent} !important`,
    fontWeight: 500,
  },
  chipUnselected: {
    backgroundColor: "#FFFFFF",
    color: "#475569",
    borderColor: "#E2E8F0",
    fontWeight: 400,
    "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#A5B4FC" },
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  starRow: {
    display: "flex",
    gap: 6,
    marginTop: 8,
  },
  starButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": { borderColor: "#FBBF24" },
  },
  starButtonActive: {
    borderColor: "#FBBF24",
    backgroundColor: "#FFFBEB",
  },
  submitButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
  locationSuggestion: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    cursor: "pointer",
    borderRadius: 8,
    marginTop: 4,
    transition: "background-color 0.15s ease",
  },
}));

export default function DetailsTab() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [detailState, setDetailState] = useState({
    title: "",
    place_type: "Hotel",
    description: "",
    location: "",
    district: "",
    address: "",
    city: "",
    checkin_time: "",
    checkout_time: "",
    country: "",
    breakfast_type: "none",
  });
  const [country, setCountry] = useState("Uganda");
  const [address, setAddress] = useState("");
  const [whatToUse, setWhatToUse] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [propertyRating, setPropertyRating] = useState(0);
  const [offerBreakfast, setOfferBreakfast] = useState("No");
  const [priceIncludesBreakfast, setPriceIncludesBreakfast] = useState("No");

  const toggleWhatToUse = (name) => {
    setWhatToUse((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const toggleLanguage = (name) => {
    setLanguages((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  return (
    <Formik
      initialValues={detailState}
      validationSchema={Yup.object().shape({
        title: Yup.string()
          .required("Please enter the property title")
          .max(100, "Title can't exceed 100 characters"),
        description: Yup.string()
          .required("Please enter a description")
          .max(250, "Description can't exceed 250 characters"),
        district: Yup.string().required("Please enter the district/city"),
        checkin_time: Yup.string().required("Please enter check-in time"),
        checkout_time: Yup.string().required("Please enter check-out time"),
      })}
      onSubmit={async (values) => {
        const obj = {
          ...values,
          place_type: detailState.place_type,
          address,
          country,
          what_to_use: whatToUse.map((name) => ({ name })),
          language: languages.map((name) => ({ name })),
          place_rating: propertyRating,
          offer_breakfast: offerBreakfast === "Yes",
          breakfast_type:
            offerBreakfast === "Yes" ? detailState.breakfast_type : "none",
          breakfast_included: priceIncludesBreakfast === "Yes",
        };
        dispatch(asyncAddHotelProperty(obj));
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Basic Information
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                Property name, category, and star rating
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    variant="outlined"
                    required
                    fullWidth
                    label="Property Name"
                    name="title"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Guests will see this when searching for your place"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.textField}
                  >
                    <InputLabel>Property Category</InputLabel>
                    <Select
                      className={classes.selectField}
                      onChange={(e) =>
                        setDetailState({
                          ...detailState,
                          place_type: e.target.value,
                        })
                      }
                      label="Property Category"
                      value={detailState.place_type}
                    >
                      {HOTEL_CATEGORIES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: TOKENS.textSecondary,
                      marginBottom: 6,
                    }}
                  >
                    Property Rating
                  </Typography>
                  <Box className={classes.starRow}>
                    {PROPERTY_RATINGS.map((rating) => (
                      <Box
                        key={rating}
                        className={`${classes.starButton} ${
                          propertyRating >= rating
                            ? classes.starButtonActive
                            : ""
                        }`}
                        onClick={() => setPropertyRating(rating)}
                      >
                        <StarIcon
                          style={{
                            fontSize: 20,
                            color:
                              propertyRating >= rating
                                ? "#FBBF24"
                                : TOKENS.textMuted,
                          }}
                        />
                      </Box>
                    ))}
                    {propertyRating > 0 && (
                      <Typography
                        style={{
                          fontSize: 13,
                          color: TOKENS.textSecondary,
                          alignSelf: "center",
                          marginLeft: 8,
                        }}
                      >
                        {propertyRating} star{propertyRating > 1 ? "s" : ""}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Location */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>Location</Typography>
              <Typography className={classes.sectionSubtitle}>
                Where is your property located?
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    disableClearable
                    fullWidth
                    options={COUNTRIES}
                    onChange={(_, newValue) => setCountry(newValue)}
                    inputValue={country}
                    onInputChange={(_, newInputValue) =>
                      setCountry(newInputValue)
                    }
                    autoHighlight
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className={classes.textField}
                        variant="outlined"
                        label="Country"
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: "new-password",
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    error={Boolean(touched.district && errors.district)}
                    helperText={touched.district && errors.district}
                    variant="outlined"
                    required
                    fullWidth
                    label="District / City"
                    name="district"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="e.g. Kampala"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    variant="outlined"
                    fullWidth
                    label="Location / Area"
                    name="location"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="e.g. Kololo, Nakasero"
                  />
                </Grid>
                <Grid item xs={12}>
                  <PlacesAutocomplete
                    value={address}
                    onChange={setAddress}
                    onSelect={setAddress}
                  >
                    {({
                      getInputProps,
                      suggestions,
                      getSuggestionItemProps,
                      loading,
                    }) => (
                      <div>
                        <TextField
                          {...getInputProps({
                            variant: "outlined",
                            label: "Google Maps Address",
                            placeholder: "Search for exact address",
                            fullWidth: true,
                            className: classes.textField,
                          })}
                        />
                        {(loading || suggestions.length > 0) && (
                          <Box
                            style={{
                              border: "1px solid #E2E8F0",
                              borderRadius: 8,
                              marginTop: 4,
                              overflow: "hidden",
                            }}
                          >
                            {loading && (
                              <Box style={{ padding: 12 }}>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Searching...
                                </Typography>
                              </Box>
                            )}
                            {suggestions.map((suggestion) => (
                              <div
                                {...getSuggestionItemProps(suggestion, {
                                  className: classes.locationSuggestion,
                                  style: {
                                    backgroundColor: suggestion.active
                                      ? "#F1F5F9"
                                      : "#FFFFFF",
                                  },
                                })}
                              >
                                <LocationCityIcon
                                  style={{
                                    marginRight: 10,
                                    color: TOKENS.textMuted,
                                    fontSize: 18,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  style={{ fontSize: 13 }}
                                >
                                  {suggestion.description}
                                </Typography>
                              </div>
                            ))}
                          </Box>
                        )}
                      </div>
                    )}
                  </PlacesAutocomplete>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Check-in / Check-out & Description */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Description & Timing
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                Tell guests about your property and when they can arrive
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    error={Boolean(touched.checkin_time && errors.checkin_time)}
                    helperText={touched.checkin_time && errors.checkin_time}
                    variant="outlined"
                    required
                    fullWidth
                    label="Check-in Time"
                    name="checkin_time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="e.g. 2:00 PM"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    error={Boolean(
                      touched.checkout_time && errors.checkout_time,
                    )}
                    helperText={touched.checkout_time && errors.checkout_time}
                    variant="outlined"
                    required
                    fullWidth
                    label="Check-out Time"
                    name="checkout_time"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="e.g. 11:00 AM"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    className={classes.textField}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    variant="outlined"
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    label="Property Description"
                    name="description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="Highlight what's special about your property, the neighborhood, and services you offer"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Guest Facilities */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Guest Facilities
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                What can guests use at your property? —{" "}
                {whatToUse.length > 0
                  ? `${whatToUse.length} selected`
                  : "none selected"}
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Box className={classes.chipGrid}>
                {WHAT_GUESTS_CAN_USE.map((name) => (
                  <Chip
                    key={name}
                    label={whatToUse.includes(name) ? `✓ ${name}` : name}
                    clickable
                    variant="outlined"
                    size="small"
                    className={
                      whatToUse.includes(name)
                        ? classes.chipSelected
                        : classes.chipUnselected
                    }
                    onClick={() => toggleWhatToUse(name)}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Languages */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Languages Spoken
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                What languages does your staff speak? —{" "}
                {languages.length > 0
                  ? `${languages.length} selected`
                  : "none selected"}
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Box className={classes.chipGrid}>
                {LANGUAGES.map((name) => (
                  <Chip
                    key={name}
                    label={languages.includes(name) ? `✓ ${name}` : name}
                    clickable
                    variant="outlined"
                    size="small"
                    className={
                      languages.includes(name)
                        ? classes.chipSelected
                        : classes.chipUnselected
                    }
                    onClick={() => toggleLanguage(name)}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Breakfast */}
          <Card className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Typography className={classes.sectionTitle}>
                Breakfast
              </Typography>
              <Typography className={classes.sectionSubtitle}>
                Do you serve breakfast to guests?
              </Typography>
            </Box>
            <CardContent className={classes.sectionContent}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    className={classes.textField}
                  >
                    <InputLabel>Serve Breakfast?</InputLabel>
                    <Select
                      className={classes.selectField}
                      onChange={(e) => setOfferBreakfast(e.target.value)}
                      label="Serve Breakfast?"
                      value={offerBreakfast}
                    >
                      {["Yes", "No"].map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {offerBreakfast === "Yes" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                      >
                        <InputLabel>Breakfast Type</InputLabel>
                        <Select
                          className={classes.selectField}
                          onChange={(e) =>
                            setDetailState({
                              ...detailState,
                              breakfast_type: e.target.value,
                            })
                          }
                          label="Breakfast Type"
                          value={detailState.breakfast_type}
                        >
                          {["Breakfast", "Half Board", "Full Board"].map(
                            (item) => (
                              <MenuItem key={item} value={item}>
                                {item}
                              </MenuItem>
                            ),
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                      >
                        <InputLabel>Included in Price?</InputLabel>
                        <Select
                          className={classes.selectField}
                          onChange={(e) =>
                            setPriceIncludesBreakfast(e.target.value)
                          }
                          label="Included in Price?"
                          value={priceIncludesBreakfast}
                        >
                          {["Yes", "No"].map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Submit */}
          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ marginTop: 8 }}
          >
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              className={classes.submitButton}
              endIcon={<ArrowForwardIcon />}
            >
              Continue to Add Rooms
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
}
